import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button, Grid, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { SidePanel, SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import { LocationReasonDeactivated } from '../../../types/model/status/LocationReasonDeactivated';
import { LocationDeactivateVO } from '../../../types/model/valueObject/location/LocationDeactivateVO';

interface DeactivateLocationDialogProps {
  open: boolean;
  adminWarning?: boolean;
  onClose: () => void;
  onSubmit: (submission: LocationDeactivateVO) => void;
  locationName?: string;
  /** Used to link to the location's active bookings in the admin warning. */
  locationId?: string;
}

// Picking this reason makes the notes required.
const OTHER_REASON_KEY = 'OTHER';

const DeactivateLocationDialog = ({
    open,
    onClose,
    onSubmit,
    locationName,
    locationId,
    adminWarning = false
}: DeactivateLocationDialogProps) => {

  const [reasonKey, setReasonKey] = useState<keyof typeof LocationReasonDeactivated | undefined>(undefined);
  const [notes, setNotes] = useState<string>('');
  
  const notesRequired = reasonKey === OTHER_REASON_KEY;
  const disableSubmit = !reasonKey || (notesRequired && !notes.trim());

  useEffect(() => {
    if (!open) {
      setReasonKey(undefined);
      setNotes('');
    }
  }, [open]);

  const handleSubmit = () => {
    if (disableSubmit) return;
    onSubmit({ 
        reasonDeactivated: reasonKey, 
        reasonDeactivatedNote: notes
    });
  };

  const title = `Deactivate Location${locationName ? ` ${locationName}` : ''}`;

  return (
    <SidePanel open={open} onClose={onClose} title={title}>
      <SidePanelContent>
        <Stack spacing={3}>
          {adminWarning && (
            <Alert severity="warning" variant="filled" role="alert">
              <AlertTitle>Warning: Active Bookings Exist</AlertTitle>
              <Typography sx={{ mb: 1.5 }}>
                This location has active bookings. All bookings should be cancelled or inactivated before deactivating this location.
              </Typography>
              <Typography sx={{ mb: 1.5 }}>
                <Link
                  component={RouterLink}
                  to={`/bookings/supplier-bookings?status=ACTIVE&locationId=${locationId ?? ''}`}
                  color="inherit"
                  sx={{ fontWeight: 'bold' }}
                >
                  View all active bookings for this location
                </Link>
              </Typography>
              <Typography>
                As an admin, you may override and proceed with deactivation.
              </Typography>
            </Alert>
          )}
          <TextField
            select
            required
            fullWidth
            variant="standard"
            label="Reason"
            value={reasonKey}
            onChange={(event) => setReasonKey(event.target.value as keyof typeof LocationReasonDeactivated)}
          >
            {Object.entries(LocationReasonDeactivated).map(([key, value]) => (
              <MenuItem key={key} value={key}>{value}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            label={notesRequired ? 'Please provide further explanation' : 'Notes'}
            required={notesRequired}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Stack>
      </SidePanelContent>
      <SidePanelFooter>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={onClose}
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={8}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={disableSubmit}
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
            >
              Deactivate Location
            </Button>
          </Grid>
        </Grid>
      </SidePanelFooter>
    </SidePanel>
  );
};

export default DeactivateLocationDialog;
